import React, { useCallback, useContext, useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { HighlightOff } from "@mui/icons-material";
import { useFormContext, useWatch } from "react-hook-form";
import _ from "lodash";
import i18next from "i18next";
import { 
  RulesContext, 
  TargetFieldCategory, 
  getAvailableOperatorOptions, 
  getFlatSubFieldsOptions,
  getTargetFieldOptionsForDocument, 
  getTargetFieldOptionsForDossier, 
  getTargetFieldOptionsForThirdParty, 
  parseDocumentTypeIdFromCriterion,
  parseDossierTypeIdFromCriterion,
  parseTargetFieldCategoryFromCriterion, 
  parseTargetFieldFromCriterion } from "../../../../../services/common/RulesUtils";
import useStyles from "../../../../../style/js-style/containers/administration/rules/RuleFormsStyle";
import {
  AttributeType,
  DocumentTypePropertyType,
  FieldDisplayVariant,
  FieldType,
  Operator
} from "../../../../../services/common/Constants";
import RuleSelectInput from "../../../../../components/rules/RuleSelectInput";
import CriterionValues from "./CriterionValues";
import TargetField from "./TargetField";
import {
  getTargetFieldOptionsForContextModels, getTargetFieldOptionsForContextDirectField,
  ContextDirectField,
  isMatchingThirdPartyTarget,
  isMatchingDossierTarget,
} from '../../../../../services/common/CalculatorUtils';


export const CriterionContext = React.createContext({
  handleTargetFieldChange: () => {}
});

const CriterionFormFields = ({
  index,
  isHovered,
  onRemove,
  fieldName,
  readOnly = false
}) => {
  const { classes } = useStyles();

  const {
    activities,
    countries,
    thirdpartyAttributes,
    dossierAttributes,
    calculatedAttributes,
    thirdPartyIndicators,
    sourceData,
    financialStrengthScores,
    financialStrengthRatings,
    showInduedScores,
    showTransparencyScore,
    showSourceData,
    showFinancialStrengthScores,
    showEsgScores,
    enableTransparencyScore,
    documentTypes,
    showEcovadisScores,
    showSecurityRatingScores,
    showCreditsafeScores,
    lang,
    contextModels
  } = useContext(RulesContext);

  const [show, setShow] = useState(false);
  const [availableOperatorOptions, setAvailableOperatorOptions] = useState();
  const [availableValueOptions, setAvailableValueOptions] = useState();

  const [variantAutoComplete, setVariantAutoComplete] = useState(false);

  const [selectedFieldType, setSelectedFieldType] = useState();
  const [selectedFieldSubType, setSelectedFieldSubType] = useState('');
  const [selectedOperator, setSelectedOperator] = useState();

  const formMethods = useFormContext();
  const { setValue, getValues, clearErrors } = formMethods;

  let currentCriterion = useWatch({ name: `${fieldName}` });

  const getAvailableTargetFields = useCallback((criterion) => {
    let availableTargetFields = [];
    const targetFieldCategory = parseTargetFieldCategoryFromCriterion(criterion);

    switch (targetFieldCategory) {
      case TargetFieldCategory.THIRD_PARTY:
        availableTargetFields =  getFlatSubFieldsOptions(getTargetFieldOptionsForThirdParty({
          countries: countries,
          activities: activities,
          thirdpartyAttributes: thirdpartyAttributes,
          calculatedAttributes: _.filter(calculatedAttributes, item => isMatchingThirdPartyTarget(item)),
          indicators: _.filter(thirdPartyIndicators, item => isMatchingThirdPartyTarget(item)),
          sourceData: sourceData,
          financialStrengthScores: financialStrengthScores,
          financialStrengthRatings: financialStrengthRatings,
          withInduedScores: showInduedScores,
          withTransparencyScore: showTransparencyScore,
          withSourceData: showSourceData,
          withFinancialStrengthScores: showFinancialStrengthScores,
          withEsgScores: showEsgScores,
          enableTransparencyScore: enableTransparencyScore,
          withEcovadisScores: showEcovadisScores,
          withSecurityRatingScores: showSecurityRatingScores,
          withCreditsafeScores: showCreditsafeScores,
          lang: lang
        }));
        break;

      case TargetFieldCategory.DOSSIER:
        availableTargetFields = getTargetFieldOptionsForDossier(
          dossierAttributes,
          _.filter(calculatedAttributes, item => isMatchingDossierTarget(item, parseDossierTypeIdFromCriterion(criterion))),
          _.filter(thirdPartyIndicators, item => isMatchingDossierTarget(item, parseDossierTypeIdFromCriterion(criterion)))
        );
        break;

      case TargetFieldCategory.DOCUMENT:
        availableTargetFields = getFlatSubFieldsOptions(getTargetFieldOptionsForDocument(
          _.find(documentTypes, { value: parseDocumentTypeIdFromCriterion(criterion) })?.properties || []
        ));
        break;

      case TargetFieldCategory.CONTEXT:
        const targetFieldData = criterion.targetField.target_field_data.data_for_context;
        if(!targetFieldData) return availableTargetFields;
        const targetFieldNature = targetFieldData.target_field_nature;
        if(ContextDirectField.includes(targetFieldNature)) {
          availableTargetFields = [getTargetFieldOptionsForContextDirectField(targetFieldNature)];
        } else {
          availableTargetFields = getFlatSubFieldsOptions(getTargetFieldOptionsForContextModels(
            contextModels,
            targetFieldData
          ));
        }
        break;

      default:
        break;
    }

    return availableTargetFields;
      }, [countries, 
          activities, 
          thirdpartyAttributes, 
          dossierAttributes,
          calculatedAttributes,
          thirdPartyIndicators, 
          sourceData,
          financialStrengthScores, 
          financialStrengthRatings, 
          showInduedScores, 
          showTransparencyScore, 
          showSourceData,
          showFinancialStrengthScores, 
          showEsgScores, 
          enableTransparencyScore, 
          documentTypes,
          showEcovadisScores,
          showSecurityRatingScores,
          showCreditsafeScores,
          lang,
          contextModels
        ]);


  const getTargetFieldEntryFromCriterion = useCallback((criterion) => {
    const field = parseTargetFieldFromCriterion(criterion);
    const targetFields = getAvailableTargetFields(criterion);
    return _.find(targetFields, { value: field });
  }, [getAvailableTargetFields]);


  const onOperatorChange = useCallback((newValue) => {
    setValue(`${fieldName}.operator`, newValue);
    setSelectedOperator(newValue);

    /*
      Criterion value initialization depends on both the field type and the applied operator.
      'values' is initialized to an empty array for cases where we expect multiple values, otherwise it is initialized 
      to an array of a single empty field.
    */
    switch (newValue) {
      case Operator.IN_SET:
      case Operator.NOT_IN_SET:
      case Operator.IS_NULL:
      case Operator.NOT_NULL:
        setValue(`${fieldName}.values`, []);
        break;

      case Operator.EQUAL:
      case Operator.NOT_EQUAL:
      case Operator.CONTAINS:
      case Operator.NOT_CONTAINS:
        let currentCriterionValue = getValues(`${fieldName}`);
        let criterionFieldEntry = getTargetFieldEntryFromCriterion(currentCriterionValue);
        if (
           (
            criterionFieldEntry.type === FieldType.ATTRIBUTE &&
            criterionFieldEntry.subType === AttributeType.LIST_MULTIPLE
           ) ||
           (
             criterionFieldEntry.type === FieldType.FORM_FIELD &&
             criterionFieldEntry.subType === DocumentTypePropertyType.CASE_A_COCHER
           )
        ) {
          setValue(`${fieldName}.values`, []);
        } else {
          setValue(`${fieldName}.values`, [""]);
        }
        break;

      default:
        setValue(`${fieldName}.values`, [""]);
        break;
    }

    //Clear all previous errors that might have been raised on 'values' field
    clearErrors(`${fieldName}.values`);
  }, [fieldName, getTargetFieldEntryFromCriterion, setValue, getValues, clearErrors]);

  const handleTargetFieldChange = useCallback((newValue) => {
    
    const currentCriterionValue = getValues(`${fieldName}`);
    const criterionFieldEntry = getTargetFieldEntryFromCriterion(currentCriterionValue);

    setSelectedFieldType(criterionFieldEntry?.type);
    setSelectedFieldSubType(criterionFieldEntry?.subType || ``);

    //Criterion operator
    const filteredOperatorOptions = getAvailableOperatorOptions(criterionFieldEntry);
    setAvailableOperatorOptions(filteredOperatorOptions);
    if (filteredOperatorOptions?.length > 0) {
      setValue(`${fieldName}.operator`, filteredOperatorOptions[0].value);
      onOperatorChange(filteredOperatorOptions[0].value);
    } else {
      setValue(`${fieldName}.operator`, "");
    }
    clearErrors(`${fieldName}.operator`);

    //Criterion values
    setAvailableValueOptions(criterionFieldEntry?.valueOptions);

    setVariantAutoComplete(
      criterionFieldEntry?.optionsDisplayVariant && criterionFieldEntry.optionsDisplayVariant === FieldDisplayVariant.AUTOCOMPLETE ? true : false
    )

    //Clear all previous errors that might have been raised on 'values' field
    clearErrors(`${fieldName}.values`);
  }, [fieldName, setValue, clearErrors, getTargetFieldEntryFromCriterion, getValues, onOperatorChange]);

  const renderDeleteButton = useCallback(() => {
    return (
      <Tooltip placement="top" title={i18next.t("delete")}>
        <IconButton
          onClick={onRemove}
          className={classes.valuesIconBtnClosed}
          size="xlarge"
        >
          <HighlightOff />
        </IconButton>
      </Tooltip>
    );
  }, [onRemove, classes]);

   // Récupère les opérateurs et valeurs des critères à la restitution ou modification des conditions
   useEffect(() => {
    const criterionFieldEntry = getTargetFieldEntryFromCriterion(currentCriterion);

    if (criterionFieldEntry !== undefined && (selectedFieldType !== criterionFieldEntry?.type || selectedFieldSubType !== criterionFieldEntry?.subType)) {
      // MAJ type
      setSelectedFieldType(criterionFieldEntry?.type);
      setSelectedFieldSubType(criterionFieldEntry?.subType || ``);
      // MAJ opérateur
      const filteredOperatorOptions = getAvailableOperatorOptions(criterionFieldEntry);
      setAvailableOperatorOptions(filteredOperatorOptions);
      // MAJ valeurs
      setAvailableValueOptions(criterionFieldEntry?.valueOptions);
      setVariantAutoComplete(criterionFieldEntry.optionsDisplayVariant && criterionFieldEntry.optionsDisplayVariant === FieldDisplayVariant.AUTOCOMPLETE
        ? true : false)
    }
  }, [currentCriterion, getTargetFieldEntryFromCriterion, selectedFieldType, selectedFieldSubType])

  useEffect(() => {
    const criterionFieldEntry = getTargetFieldEntryFromCriterion(currentCriterion);
    const bShow = criterionFieldEntry !== undefined;
    setShow(bShow);

    setSelectedFieldType(criterionFieldEntry?.type);
    setSelectedFieldSubType(criterionFieldEntry?.subType || ``);
    setSelectedOperator(currentCriterion?.operator);

  }, [getTargetFieldEntryFromCriterion, currentCriterion]);

  const criterionContext = React.useMemo(
    () => ({
      handleTargetFieldChange: handleTargetFieldChange,
      readOnly: readOnly
    }),
    [handleTargetFieldChange, readOnly]
  );

  return (
    <div style={{display: 'flex'}}>
      <div className={classes.ruleCriteriaDeco}/>

      <div className={classes.criteriaInputsFlex}>

        <div className={classes.flexStart} style={{minWidth: "60%", marginRight: "15px" }}>
          <CriterionContext.Provider value={criterionContext}>
            <TargetField 
              fieldName={`${fieldName}.targetField`}
              readOnly={readOnly}
            />
          </CriterionContext.Provider>
        </div>

        <div className={classes.flexStart} style={{minWidth: "35%"}}>
          <div style={{paddingTop: "0 !important", minWidth: "25%"}}>
            {show && 
            <RuleSelectInput
              fieldName={`${fieldName}.operator`}
              label={`${i18next.t(
                "rules.configuration.criteria.fields.operator.label"
              )}`}
              placeholder={i18next.t(
                `rules.configuration.criteria.fields.operator.placeholder`
              )}
              options={availableOperatorOptions}
              onChange={(newValue) => {
                onOperatorChange(newValue);
              }}
              validate={() => {
                return null;
              }}
              labelVariant="chip"
              disabled={readOnly}
            />
            }
          </div>

          <div style={{marginLeft: "15px", flex: '1 1 0' }}>
            <CriterionValues
              fieldName={`${fieldName}.values`}
              showLabel
              index={index}
              availableValueOptions={availableValueOptions}
              selectedFieldType={selectedFieldType}
              selectedFieldSubType={selectedFieldSubType}
              selectedOperator={selectedOperator}
              variantAutoComplete={variantAutoComplete}
              readOnly={readOnly}
            />
          </div>
        </div>
      </div>

      <div style={{alignSelf: "center", minWidth: "40px"}}>
        {/*Temporary. This should be managed as it is done for attribute values (using styles)*/}
        {!readOnly && isHovered && renderDeleteButton()}
      </div>
    </div>
  );
};

export default CriterionFormFields;
